import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, Clock, User, Mail, CheckCircle, AlertCircle, CheckCheck } from 'lucide-react';

const f = "'Inter', ui-sans-serif, system-ui, sans-serif";

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata'
  });
}

export default function MentionedToMe() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => sessionStorage.getItem('hcmsToken') || localStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('token') || '';

  useEffect(() => {
    loadMentions();
  }, []);

  const loadMentions = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch('/api/v2/cases/mentions', { headers });
      const data = await res.json();
      if (res.ok) {
        setMentions(data.mentions || []);
      } else {
        setError(data.message || 'Failed to load mentions');
      }
    } catch (e) {
      console.error('Failed to load mentions:', e);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (mentionId) => {
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const res = await fetch(`/api/v2/cases/mentions/${mentionId}/read`, {
        method: 'PATCH',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        setMentions(prev => prev.map(m => String(m.mention_id) === String(mentionId) ? { ...m, is_read: true } : m));
      } else {
        console.error('Failed to mark as read:', data.message || res.status);
      }
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadMentions = mentions.filter(m => !m.is_read);
    for (const mention of unreadMentions) {
      await handleMarkAsRead(mention.mention_id);
    }
  };

  const handleMentionClick = (mention) => {
    if (!mention.is_read) {
      handleMarkAsRead(mention.mention_id);
    }
    navigate(`/hcms/tickets/${mention.ticket_id}`);
  };

  return (
    <div style={{ fontFamily: f, padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: '#64748B',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          padding: 0
        }}
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 24px 0' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: 0 }}>
          Mentioned To Me
        </h1>
        {mentions.some(m => !m.is_read) && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              color: '#0F172A',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = '#F8FAFC'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#FFFFFF'; }}
          >
            <CheckCheck size={15} color='#0F172A' />
            Mark all as read
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>
          Loading mentions...
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 8,
          color: '#EF4444',
          fontSize: 14,
          marginBottom: 16
        }}>
          {error}
        </div>
      )}

      {!loading && !error && mentions.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 60,
          background: '#F8FAFC',
          borderRadius: 12,
          border: '1px dashed #E2E8F0'
        }}>
          <Mail size={48} color="#94A3B8" style={{ marginBottom: 16 }} />
          <p style={{ fontSize: 16, color: '#64748B', margin: 0 }}>
            No mentions yet
          </p>
          <p style={{ fontSize: 14, color: '#94A3B8', marginTop: 8 }}>
            When someone mentions you in an internal note, it will appear here.
          </p>
        </div>
      )}

      {!loading && !error && mentions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mentions.map((mention) => (
            <div
              key={mention.mention_id}
              onClick={() => handleMentionClick(mention)}
              style={{
                background: '#FFFFFF',
                border: `1px solid ${mention.is_read ? '#E2E8F0' : '#F59E0B'}`,
                borderRadius: 12,
                padding: 20,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: mention.is_read ? '0 1px 2px rgba(0,0,0,0.04)' : '0 2px 8px rgba(245, 158, 11, 0.15)',
                position: 'relative'
              }}
            >
              {!mention.is_read && (
                <div style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#F59E0B'
                }} />
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#64748B',
                      background: '#F1F5F9',
                      padding: '4px 8px',
                      borderRadius: 6
                    }}>
                      #{mention.ticket_code}
                    </span>
                    <span style={{
                      fontSize: 12,
                      color: '#64748B',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <Clock size={12} />
                      {formatDate(mention.mentioned_at)}
                    </span>
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: '4px 0' }}>
                    {mention.ticket_title}
                  </h3>
                </div>
                {mention.is_read ? (
                  <CheckCircle size={20} color="#22C55E" />
                ) : (
                  <AlertCircle size={20} color="#F59E0B" />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <User size={14} color="#64748B" />
                <span style={{ fontSize: 13, color: '#64748B' }}>
                  Mentioned by <strong>{mention.mentioned_by_name}</strong>
                </span>
              </div>

              <div style={{
                background: '#F8FAFC',
                padding: 12,
                borderRadius: 8,
                borderLeft: '3px solid #E2E8F0'
              }}>
                <p style={{
                  fontSize: 13,
                  color: '#475569',
                  margin: 0,
                  fontStyle: 'italic',
                  lineHeight: 1.5
                }}>
                  "{mention.note_text?.substring(0, 150)}{mention.note_text?.length > 150 ? '...' : ''}"
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <span style={{
                  fontSize: 12,
                  color: mention.is_read ? '#22C55E' : '#F59E0B',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  {mention.is_read ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                  {mention.is_read ? 'Read' : 'Unread'}
                </span>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>
                  •
                </span>
                <span style={{ fontSize: 12, color: '#64748B' }}>
                  Status: <strong style={{ textTransform: 'capitalize' }}>{mention.ticket_status}</strong>
                </span>
                {!mention.is_read && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMarkAsRead(mention.mention_id); }}
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      background: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#0F172A',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#FFFFFF'; }}
                  >
                    <CheckCircle size={12} color="#22C55E" />
                    Mark as read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
